import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './style1.css';

interface Program {
    id: number;
    name: string;
    url: string;
}

interface FileNode {
    name: string;
    path: string;
    isDirectory: boolean;
    children?: FileNode[];
}

interface ProgramsProps {
    username?: string;
    onLogout?: () => void;
}

const navLinks = [
    { name: 'HOME', href: '/' },
    { name: 'PROGRAMS', href: '/programs' },
    { name: 'VIDEOS', href: '/videos' },
    { name: 'IMAGES', href: '/images' },
    { name: 'DEMOS', href: '/demos' },
    { name: 'CERTIFICATES', href: '/certificates' },
    { name: 'ABOUT', href: '/about' },
];

const Programs: React.FC<ProgramsProps> = ({ username, onLogout }) => {
    const [programs, setPrograms] = useState<Program[]>([]);
    const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
    const [files, setFiles] = useState<FileNode[]>([]);
    const [currentPath, setCurrentPath] = useState<string>('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [showSource, setShowSource] = useState(false);
    const [sourceContent, setSourceContent] = useState('');
    const [sourceFileName, setSourceFileName] = useState('');
    const [videoExists, setVideoExists] = useState(false);
    const [imageExists, setImageExists] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        fetch('/allprograms', {
            headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
        })
            .then(res => res.json())
            .then(data => setPrograms(data))
            .catch(() => setPrograms([]));
    }, []);

    useEffect(() => {
        if (selectedProgram) {
            const token = localStorage.getItem('token');
            fetch(`/listfiles/${selectedProgram.name}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
            })
                .then(res => res.json())
                .then(data => setFiles(data))
                .catch(() => setFiles([]));
            setCurrentPath('');

            // Check for video existence
            fetch(`/videos/exists/${encodeURIComponent(selectedProgram.name)}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
            })
                .then(res => res.json())
                .then(exists => setVideoExists(!!exists))
                .catch(() => setVideoExists(false));

            // Check for image existence
            fetch(`/images/exists/${encodeURIComponent(selectedProgram.name)}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
            })
                .then(res => res.json())
                .then(exists => setImageExists(!!exists))
                .catch(() => setImageExists(false));
        }
    }, [selectedProgram]);

    const getCurrentFiles = (): FileNode[] => {
        if (!currentPath) return files;
        const segments = currentPath.split('/').filter(Boolean);
        let nodes = files;
        let pathSoFar = '';
        for (const segment of segments) {
            pathSoFar = pathSoFar ? `${pathSoFar}/${segment}` : segment;
            const next = nodes.find(n => n.path === pathSoFar && (n.isDirectory || (n.children && n.children.length > 0)));
            if (next && next.children) {
                nodes = next.children;
            } else {
                return [];
            }
        }
        return nodes;
    };

    const handleFolderClick = (node: FileNode) => {
        setCurrentPath(node.path);
    };

    const handleFileClick = (node: FileNode) => {
        setSourceFileName(node.name);
        const token = localStorage.getItem('token');
        fetch(`/programfile/${selectedProgram?.name}?path=${encodeURIComponent(node.path)}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
        })
            .then(res => res.text())
            .then(text => {
                setSourceContent(text);
                setShowSource(true);
            })
            .catch(() => {
                setSourceContent('Failed to load file.');
                setShowSource(true);
            });
    };

    const handleGoUp = () => {
        if (!currentPath) return;
        const segments = currentPath.split('/').filter(Boolean);
        segments.pop();
        setCurrentPath(segments.join('/'));
    };

    const handleBack = () => {
        setSelectedProgram(null);
        setFiles([]);
        setCurrentPath('');
        setVideoExists(false);
        setImageExists(false);
    };

    const isFolder = (node: FileNode) =>
        node.isDirectory || (node.children && node.children.length > 0);

    const renderFiles = (nodes: FileNode[]) => (
        <ul style={{ listStyle: 'none', paddingLeft: 20 }}>
            {nodes.map(node => (
                <li key={node.path}>
                    <button
                        style={{
                            background: '#fff',
                            border: '1px solid #2575fc44',
                            borderRadius: '10px',
                            padding: '0.4rem 1rem',
                            margin: '0.2rem 0',
                            color: isFolder(node) ? '#2575fc' : '#222',
                            fontWeight: isFolder(node) ? 'bold' : 'normal',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            fontSize: '1rem',
                            textAlign: 'left',
                            width: '100%',
                            transition: 'background 0.2s'
                        }}
                        onClick={() => {
                            if (isFolder(node)) {
                                handleFolderClick(node);
                            } else {
                                handleFileClick(node);
                            }
                        }}
                    >
                        {isFolder(node) ? '📁' : '📄'} {node.name}
                    </button>
                </li>
            ))}
        </ul>
    );

    const getCurrentPathString = () => {
        if (!currentPath) return '';
        return ' / ' + currentPath;
    };

    const handleViewImages = () => {
        navigate(`/images/${encodeURIComponent(selectedProgram?.name || '')}`);
    };

    // FIX: Pass state to auto-select video in Videos page
    const handleViewVideos = () => {
        navigate('/videos', { state: { autoSelectProgram: selectedProgram?.name } });
    };

    return (
        <div className="modern-layout">
            <header className="modern-header">
                <div className="header-container">
                    <nav className={`modern-nav ${menuOpen ? 'open' : ''}`}>
                        {navLinks.map(link =>
                            link.href.startsWith('/')
                                ? <Link
                                    key={link.name}
                                    to={link.href}
                                    className="modern-nav-link"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    {link.name}
                                  </Link>
                                : <a
                                    key={link.name}
                                    href={link.href}
                                    className="modern-nav-link"
                                  >
                                    {link.name}
                                  </a>
                        )}
                        <button className="logout-btn" onClick={onLogout}>LOG OUT</button>
                    </nav>
                    <button
                        className="menu-toggle"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Toggle menu"
                    >
                        <span className="menu-icon">&#9776;</span>
                    </button>
                </div>
            </header>
            <main className="modern-content">
                <h2 style={{ textAlign: 'center' }}>Programs</h2>
                {!selectedProgram ? (
                    <ul style={{ listStyle: 'none', padding: 0, width: '100%' }}>
                        {[...programs].sort((a, b) => b.id - a.id).map(program => (
                            <li key={program.id} style={{ margin: '0.5rem 0' }}>
                                <button
                                    style={{
                                        width: '100%',
                                        padding: '0.7rem 1rem',
                                        borderRadius: '12px',
                                        border: '1px solid #2575fc44',
                                        background: '#fff',
                                        color: '#2575fc',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        transition: 'background 0.2s',
                                        font: 'inherit'
                                    }}
                                    onClick={() => setSelectedProgram(program)}
                                >
                                    {program.name}
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {videoExists && (
                            <button
                                style={{
                                    width: '100%',
                                    padding: '0.7rem 1rem',
                                    borderRadius: '12px',
                                    border: '1px solid #2575fc44',
                                    background: '#fff',
                                    color: '#2575fc',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    transition: 'background 0.2s',
                                    font: 'inherit',
                                    marginBottom: '0.5rem'
                                }}
                                onClick={handleViewVideos}
                            >
                                Click to view videos of this application
                            </button>
                        )}
                        {imageExists && (
                            <button
                                style={{
                                    width: '100%',
                                    padding: '0.7rem 1rem',
                                    borderRadius: '12px',
                                    border: '1px solid #2575fc44',
                                    background: '#fff',
                                    color: '#2575fc',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    transition: 'background 0.2s',
                                    font: 'inherit',
                                    marginBottom: '0.5rem'
                                }}
                                onClick={handleViewImages}
                            >
                                Click to view images of this application
                            </button>
                        )}
                        <button
                            onClick={handleBack}
                            style={{
                                marginBottom: '1.5rem',
                                padding: '0.5rem 1.2rem',
                                borderRadius: '10px',
                                border: 'none',
                                background: '#2575fc',
                                color: '#fff',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                display: 'block',
                                marginLeft: 'auto',
                                marginRight: 'auto'
                            }}
                        >
                            ← Back to Programs
                        </button>
                        <div style={{
                            background: '#fff',
                            borderRadius: '16px',
                            boxShadow: '0 2px 16px #6a11cb22',
                            padding: '1.5rem',
                            marginBottom: '2rem',
                            width: '100%',
                            maxWidth: 600
                        }}>
                            <h3 style={{ color: '#2575fc', marginBottom: '1rem' }}>
                                Files in {selectedProgram.name}
                                <span style={{ fontWeight: 'normal', color: '#888', fontSize: '0.9rem' }}>
                                    {getCurrentPathString()}
                                </span>
                            </h3>
                            {currentPath && (
                                <button
                                    onClick={handleGoUp}
                                    style={{
                                        marginBottom: '1rem',
                                        padding: '0.3rem 1rem',
                                        borderRadius: '8px',
                                        border: 'none',
                                        background: '#eee',
                                        color: '#2575fc',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    ↑ Go Up
                                </button>
                            )}
                            {getCurrentFiles().length > 0 ? renderFiles(getCurrentFiles()) : <div>No files found.</div>}
                        </div>
                    </div>
                )}
                {showSource && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            background: 'rgba(0,0,0,1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000,
                            padding: 0
                        }}
                        onClick={() => setShowSource(false)}
                    >
                        <div
                            style={{
                                background: '#222',
                                borderRadius: '18px',
                                boxShadow: '0 2px 32px #6a11cb88',
                                padding: window.innerWidth <= 900 ? '0.7rem' : '2rem',
                                maxWidth: window.innerWidth <= 900 ? '98vw' : '90vw',
                                maxHeight: window.innerWidth <= 900 ? '90vh' : '90vh',
                                minWidth: window.innerWidth <= 900 ? undefined : '400px',
                                minHeight: '180px',
                                overflow: 'auto',
                                color: '#fff',
                                fontFamily: 'monospace',
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                            onClick={e => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setShowSource(false)}
                                style={{
                                    position: 'absolute',
                                    top: window.innerWidth <= 900 ? 8 : 18,
                                    right: window.innerWidth <= 900 ? 8 : 18,
                                    background: '#444',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.4rem 1rem 0.4rem 0.8rem',
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    zIndex: 2
                                }}
                                aria-label="Close window"
                            >
                                <span style={{ fontSize: '1.2em' }}>❌</span> Close Window
                            </button>
                            <div style={{ fontWeight: 'bold', marginBottom: '1rem', color: '#ffb347', marginTop: window.innerWidth <= 900 ? '0.7rem' : '1.5rem', fontSize: window.innerWidth <= 900 ? '1rem' : undefined }}>
                                {sourceFileName}
                            </div>
                            <pre style={{
                                whiteSpace: window.innerWidth <= 900 ? 'pre' : 'pre-wrap',
                                wordBreak: window.innerWidth <= 900 ? 'normal' : 'break-all',
                                fontSize: window.innerWidth <= 900 ? '0.92rem' : '1rem',
                                margin: 0,
                                flex: 1,
                                overflowX: 'auto',
                                maxWidth: '100vw',
                                minWidth: 0
                            }}>
                                {sourceContent}
                            </pre>
                        </div>
                    </div>
                )}
            </main>
            <footer className="modern-footer">
                &copy; {new Date().getFullYear()} Timo
            </footer>
        </div>
    );
};

export default Programs;